import { useState, useEffect, useRef, useCallback } from 'react';
import { textToSpeechService } from '../services/textToSpeechService';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { splitBookIntoSentences } from '../utils/sentenceSegmenter';
import { MULTILANG_HOMOPHONES } from '../utils/homophonesDictionary';
import { dbManager } from '../services/db_manager';
import { getLocalISOString } from '../utils/timezoneUtils';

export interface WordStatus {
  id: string;
  word: string;
  normalized: string;
  status: 'pending' | 'correct' | 'incorrect';
}

const SUCCESS_SOUND_URL = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/pronuncia-cards/assets/success.ogg';

const BCP47_MAP: Record<string, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  es: 'es-ES',
  de: 'de-DE',
  it: 'it-IT',
  pt: 'pt-BR',
  ru: 'ru-RU',
  ja: 'ja-JP',
  zh: 'zh-CN',
  hu: 'hu-HU',
  nl: 'nl-NL',
  ar: 'ar-SA',
  ko: 'ko-KR',
  pl: 'pl-PL'
};

export const logShadowingSessionTime = async (userId: string, targetLang: string, durationSeconds: number) => {
  if (!userId || userId === 'guest' || userId === 'null' || userId === 'undefined' || durationSeconds <= 0) return;
  const cleanLang = targetLang.split(/[-_]/)[0].toLowerCase();
  const todayStr = getLocalISOString().split('T')[0];

  try {
    const existing: any = await dbManager
      .from('shadowing_logs', userId)
      .select({ target_lang: cleanLang, activity_date: todayStr });

    if (Array.isArray(existing) && existing.length > 0) {
      const row = existing[0];
      const newDuration = (Number(row.duration) || 0) + Math.round(durationSeconds);
      await dbManager.from('shadowing_logs', userId).update({
        duration: newDuration,
        created_at: getLocalISOString()
      }, { id: row.id });
    } else {
      const newId = `shadow_${userId}_${cleanLang}_${todayStr}`;
      await dbManager.from('shadowing_logs', userId).insert({
        id: newId,
        user_id: userId,
        target_lang: cleanLang,
        duration: Math.round(durationSeconds),
        activity_date: todayStr,
        created_at: getLocalISOString()
      });
    }
  } catch (e) {
    console.warn('[ShadowingTracker] Error logging shadowing time:', e);
  }
};

const NUMBER_WORD_MAP: Record<string, string> = {
  'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5',
  'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10',
  'eleven': '11', 'twelve': '12', 'thirteen': '13', 'fourteen': '14', 'fifteen': '15',
  'sixteen': '16', 'seventeen': '17', 'eighteen': '18', 'nineteen': '19', 'twenty': '20',
  'zéro': '0', 'un': '1', 'une': '1', 'deux': '2', 'trois': '3', 'quatre': '4',
  'cinq': '5', 'sept': '7', 'huit': '8', 'neuf': '9', 'dix': '10',
  'onze': '11', 'douze': '12', 'treize': '13', 'quatorze': '14', 'quinze': '15',
  'seize': '16', 'dix-sept': '17', 'dix-huit': '18', 'dix-neuf': '19', 'vingt': '20',
  'cero': '0', 'uno': '1', 'una': '1', 'dos': '2', 'tres': '3', 'cuatro': '4',
  'cinco': '5', 'seis': '6', 'siete': '7', 'ocho': '8', 'nueve': '9', 'diez': '10',
  'um': '1', 'uma': '1', 'dois': '2', 'duas': '2', 'três': '3', 'quatro': '4',
  'cinco': '5', 'sete': '7', 'oito': '8', 'nove': '9', 'dez': '10',
  'null': '0', 'eins': '1', 'ein': '1', 'zwei': '2', 'drei': '3', 'vier': '4',
  'fünf': '5', 'sechs': '6', 'sieben': '7', 'acht': '8', 'neun': '9', 'zehn': '10',
  'due': '2', 'tre': '3', 'quattro': '4', 'cinque': '5', 'sette': '7', 'otto': '8', 'dieci': '10'
};

export function cleanToken(word: string): string {
  if (!word) return '';
  return word
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove acentos diacríticos
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"«»!]/g, "") // Remove pontuações
    .trim();
}

export function normalizeNumberToken(token: string): string {
  const c = cleanToken(token);
  if (!c) return '';
  if (NUMBER_WORD_MAP[c]) return NUMBER_WORD_MAP[c];
  return c;
}

export function stripGrammaticalSuffix(word: string): string {
  const w = cleanToken(word);
  if (w.length <= 3) return w;
  return w.replace(/(es|s|e|x|nt|ed)$/i, '');
}

const getLevenshteinDistance = (a: string, b: string): number => {
  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

export const getSimilarity = (s1: string, s2: string): number => {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - getLevenshteinDistance(longer, shorter)) / parseFloat(longerLength.toString());
};

export function isWordMatch(target: string, spoken: string, langCode: string = 'en'): boolean {
  const t = cleanToken(target);
  const s = cleanToken(spoken);
  const cleanLang = langCode.split(/[-_]/)[0].toLowerCase();

  if (!t || !s) return false;
  if (t === s) return true;

  // 1. Normalização numérica (cinq <-> 5, three <-> 3, dix <-> 10, etc.)
  const numT = normalizeNumberToken(t);
  const numS = normalizeNumberToken(s);
  if (numT && numS && numT === numS) return true;

  // 2. Remover hífens, pontuações e espaços residuais
  const tNoHyphen = cleanToken(target.replace(/[-–—'’]/g, ''));
  const sNoHyphen = cleanToken(spoken.replace(/[-–—'’]/g, ''));
  if (tNoHyphen && sNoHyphen && tNoHyphen === sNoHyphen) return true;

  // 3. Verificação no dicionário de homófonos do idioma
  const langHomophones = MULTILANG_HOMOPHONES[cleanLang];
  if (langHomophones) {
    const validEquivalents = langHomophones[t];
    if (validEquivalents && validEquivalents.map(cleanToken).includes(s)) {
      return true;
    }
  }

  // 4. Inicial de 1 letra (ex: "C.", "B.")
  if (t.length === 1) {
    if (langHomophones && langHomophones[t]?.map(cleanToken).includes(s)) return true;
    return t === s;
  }

  // 5. Normalização de raiz/sufixo gramatical (plurais silenciosos, e mudo ex: noires -> noir)
  const stemT = stripGrammaticalSuffix(t);
  const stemS = stripGrammaticalSuffix(s);
  if (stemT && stemS && stemT === stemS && Math.abs(t.length - s.length) <= 2) {
    return true;
  }

  // 6. Tolerância flexível por Levenshtein (>= 85% de similaridade ou <= 1 caractere de diferença para palavras curtas)
  if (t.length <= 4) {
    return getLevenshteinDistance(t, s) <= 1;
  }

  const similarity = getSimilarity(t, s);
  if (similarity >= 0.85) return true;

  // Permite distância de Levenshtein <= 2 para palavras de 5+ caracteres
  if (t.length >= 5 && getLevenshteinDistance(t, s) <= 2) return true;

  return false;
}

export function checkContractionMatch(targetWord: string, spokenTokens: string[], startIndex: number, langCode: string = 'en'): { isMatch: boolean; tokensConsumed: number } {
  if (startIndex >= spokenTokens.length) return { isMatch: false, tokensConsumed: 0 };
  
  const token0 = spokenTokens[startIndex];
  
  // 1. Direct match with homophones & cleanToken
  if (isWordMatch(targetWord, token0, langCode)) {
    return { isMatch: true, tokensConsumed: 1 };
  }

  // 2. Compound/Hyphenated words or Apostrophe contractions (ex: Marché-Neuf, d'aventures, l'homme, c'est)
  if (targetWord.includes("'") || targetWord.includes("’") || targetWord.includes("-") || targetWord.includes("–")) {
    const rawCleaned = cleanToken(targetWord.replace(/['’\-–—]/g, ""));
    if (isWordMatch(rawCleaned, token0, langCode)) {
      return { isMatch: true, tokensConsumed: 1 };
    }

    const parts = targetWord.split(/['’\-–—]/).map(cleanToken).filter(Boolean);
    
    // Check base part
    if (parts.length > 1 && isWordMatch(parts[parts.length - 1], token0, langCode)) {
      return { isMatch: true, tokensConsumed: 1 };
    }

    // Check 2 consecutive spoken tokens (e.g. token0="Marché", token1="Neuf")
    if (parts.length >= 2 && startIndex + 1 < spokenTokens.length) {
      const token1 = spokenTokens[startIndex + 1];
      if (isWordMatch(parts[0], token0, langCode) && isWordMatch(parts[1], token1, langCode)) {
        return { isMatch: true, tokensConsumed: 2 };
      }
      
      const combinedSpoken = cleanToken(token0 + token1);
      if (isWordMatch(rawCleaned, combinedSpoken, langCode)) {
        return { isMatch: true, tokensConsumed: 2 };
      }
    }
  }

  return { isMatch: false, tokensConsumed: 0 };
}

export interface UseShadowingSentenceTrackerOptions {
  locale?: string;
  userId?: string;
  onSectionFinished?: () => void;
}

export const useShadowingSentenceTracker = ({
  locale = 'en-US',
  userId = 'guest',
  onSectionFinished
}: UseShadowingSentenceTrackerOptions) => {
  const [isShadowingActive, setIsShadowingActive] = useState(false);
  const [sentences, setSentences] = useState<string[]>([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [wordStatuses, setWordStatuses] = useState<WordStatus[]>([]);
  
  const [isListening, setIsListening] = useState(false);
  const [isTtsPlaying, setIsTtsPlaying] = useState(false);
  const [hasTtsFinished, setHasTtsFinished] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSentenceComplete, setIsSentenceComplete] = useState(false);

  // Speed rate state: 1.0, 0.75, 0.5
  const [ttsRate, setTtsRate] = useState<number>(1.0);

  // Active time tracking & 30s idle freeze
  const accumulatedSecondsRef = useRef<number>(0);
  const lastActivityTimestampRef = useRef<number>(Date.now());
  const recognitionRef = useRef<any>(null);
  const isAdvancingRef = useRef(false);
  const wordStatusesRef = useRef<WordStatus[]>([]);
  wordStatusesRef.current = wordStatuses;

  const registerUserActivity = useCallback(() => {
    lastActivityTimestampRef.current = Date.now();
  }, []);

  // Flush accumulated shadowing active seconds
  const flushShadowingTime = useCallback(() => {
    if (accumulatedSecondsRef.current > 0 && userId && userId !== 'guest') {
      const secsToFlush = accumulatedSecondsRef.current;
      accumulatedSecondsRef.current = 0;
      logShadowingSessionTime(userId, locale, secsToFlush);
    }
  }, [userId, locale]);

  // Shadowing active timer loop (30s idle freeze, only on reader routes)
  useEffect(() => {
    if (!isShadowingActive) return;

    const events = ['click', 'touchstart', 'pointerdown', 'mousemove', 'keydown', 'scroll'];
    const handleEvent = () => registerUserActivity();
    events.forEach(e => window.addEventListener(e, handleEvent, { passive: true }));

    const timer = setInterval(() => {
      if (typeof window === 'undefined') return;
      const path = window.location.pathname.toLowerCase();
      const isReader = path.includes('/reader') || path.includes('/librirui-reader');
      
      if (isReader && (Date.now() - lastActivityTimestampRef.current < 30000)) {
        accumulatedSecondsRef.current += 1;
        
        // Auto-flush every 15 active seconds
        if (accumulatedSecondsRef.current >= 15) {
          flushShadowingTime();
        }
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      events.forEach(e => window.removeEventListener(e, handleEvent));
      flushShadowingTime();
    };
  }, [isShadowingActive, registerUserActivity, flushShadowingTime]);

  const cycleTtsRate = useCallback(() => {
    registerUserActivity();
    setTtsRate(prev => {
      if (prev === 1.0) return 0.75;
      if (prev === 0.75) return 0.5;
      return 1.0;
    });
  }, [registerUserActivity]);

  // Initialize word statuses for current sentence
  const initWordStatuses = useCallback((sentenceText: string) => {
    if (!sentenceText) {
      setWordStatuses([]);
      return;
    }
    const words = sentenceText.split(/\s+/).filter(Boolean).map((w, idx) => ({
      id: `${idx}-${w}`,
      word: w,
      normalized: cleanToken(w),
      status: 'pending' as const
    }));
    setWordStatuses(words);
    setIsSentenceComplete(false);
    setHasTtsFinished(false);
    setTranscript('');
    isAdvancingRef.current = false;
  }, []);

  const setPageText = useCallback((rawText: string, langLocale: string = 'en-US', initialSentenceIdx: number = 0) => {
    const list = splitBookIntoSentences(rawText, langLocale);
    setSentences(list);
    const targetIdx = Math.max(0, Math.min(initialSentenceIdx, list.length - 1));
    setCurrentSentenceIndex(targetIdx);
    if (list.length > 0 && list[targetIdx]) {
      initWordStatuses(list[targetIdx]);
    } else {
      setWordStatuses([]);
    }
  }, [initWordStatuses]);

  const currentSentence = sentences[currentSentenceIndex] || '';

  useEffect(() => {
    if (sentences[currentSentenceIndex]) {
      initWordStatuses(sentences[currentSentenceIndex]);
    }
  }, [currentSentenceIndex, sentences, initWordStatuses]);

  // TTS Triggers
  const playSentenceTts = useCallback(async () => {
    if (!currentSentence) return;
    registerUserActivity();
    setIsTtsPlaying(true);
    setHasTtsFinished(false);

    try {
      await textToSpeechService.speak(currentSentence, locale, ttsRate);
    } catch (e) {
      console.warn('[Shadowing] TTS play fallback:', e);
    } finally {
      setIsTtsPlaying(false);
      setHasTtsFinished(true);
    }
  }, [currentSentence, locale, ttsRate, registerUserActivity]);

  const playWordTts = useCallback(async (word: string) => {
    if (!word) return;
    registerUserActivity();
    const cleanW = cleanToken(word) || word;
    try {
      await textToSpeechService.speak(cleanW, locale, ttsRate);
    } catch (e) {
      console.warn('[Shadowing] Word TTS play error:', e);
    }
  }, [locale, ttsRate, registerUserActivity]);

  const playSuccessSound = useCallback(async () => {
    try {
      const audio = new Audio(SUCCESS_SOUND_URL);
      audio.volume = 0.2;
      await audio.play();
    } catch (e) {
      console.debug('[Shadowing] Audio playback muted/suppressed.');
    }
  }, []);

  const checkSentenceCompletion = useCallback((currentStatuses: WordStatus[]) => {
    if (isAdvancingRef.current || currentStatuses.length === 0) return;

    const allCorrect = currentStatuses.every(w => w.status === 'correct');
    if (allCorrect) {
      isAdvancingRef.current = true;
      setIsSentenceComplete(true);
      
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      setIsListening(false);

      playSuccessSound();

      const langShort = locale.split(/[-_]/)[0].toLowerCase();
      activeStudyTracker.logExercisePrecision(true, userId, langShort);
      activeStudyTracker.logConsistencySession(userId, langShort);

      setTimeout(() => {
        if (currentSentenceIndex < sentences.length - 1) {
          setCurrentSentenceIndex(prev => prev + 1);
        } else {
          onSectionFinished?.();
        }
      }, 1500);
    }
  }, [sentences.length, currentSentenceIndex, locale, userId, onSectionFinished, playSuccessSound]);

  const processTranscript = useCallback((text: string, isFinal: boolean = false) => {
    registerUserActivity();
    setTranscript(text);
    if (!text.trim()) return;

    const spokenTokens = text
      .split(/\s+/)
      .map(cleanToken)
      .filter(Boolean);

    if (spokenTokens.length === 0) return;

    setWordStatuses(prevStatuses => {
      const nextStatuses = prevStatuses.map(w => ({ ...w }));
      let lastMatchIdx = -1;

      let tokenIdx = 0;
      while (tokenIdx < spokenTokens.length) {
        let matchedInLoop = false;

        for (let i = 0; i < nextStatuses.length; i++) {
          const item = nextStatuses[i];
          if (item.status === 'correct') continue;

          const matchResult = checkContractionMatch(item.word, spokenTokens, tokenIdx, locale);
          if (matchResult.isMatch) {
            item.status = 'correct';
            lastMatchIdx = i;
            tokenIdx += matchResult.tokensConsumed;
            matchedInLoop = true;
            break;
          }
        }

        if (!matchedInLoop) {
          tokenIdx++;
        }
      }

      if (isFinal && lastMatchIdx >= 0) {
        for (let i = 0; i < lastMatchIdx; i++) {
          if (nextStatuses[i].status === 'pending') {
            nextStatuses[i].status = 'incorrect';
          }
        }
      }

      checkSentenceCompletion(nextStatuses);
      return nextStatuses;
    });
  }, [checkSentenceCompletion, locale, registerUserActivity]);

  const stopRecording = useCallback(() => {
    registerUserActivity();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, [registerUserActivity]);

  const startRecording = useCallback(async () => {
    registerUserActivity();
    if (isListening) {
      stopRecording();
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.warn('[Shadowing] Mic permission denied:', e);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      try {
        const { whisperSTTService } = await import('../services/WhisperSTTService');
        console.log('[Shadowing STT] Browser recognition absent. Using WhisperSTTService fallback.');
      } catch (e) {
        console.warn('[Shadowing STT] Browser speech recognition not supported.');
      }
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      
      const cleanLangCode = locale.split(/[-_]/)[0].toLowerCase();
      const strictLang = BCP47_MAP[cleanLangCode] || (locale.includes('-') ? locale : 'en-US');
      recognition.lang = strictLang;

      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onstart = () => {
        setIsListening(true);
        setTranscript('');
      };

      recognition.onresult = (event: any) => {
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            final += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }

        const currentText = (final + interim).trim();
        if (currentText) {
          processTranscript(currentText, final.length > 0);
        }
      };

      recognition.onerror = (event: any) => {
        console.warn('[Shadowing STT Error]:', event.error);
        if (event.error !== 'no-speech') {
          setIsListening(false);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('[Shadowing STT Initialization Error]:', err);
      setIsListening(false);
    }
  }, [isListening, locale, processTranscript, stopRecording, registerUserActivity]);

  const toggleShadowing = useCallback(() => {
    registerUserActivity();
    setIsShadowingActive(prev => {
      const next = !prev;
      if (!next) {
        stopRecording();
        window.speechSynthesis.cancel();
        flushShadowingTime();
      }
      return next;
    });
  }, [stopRecording, flushShadowingTime, registerUserActivity]);

  const jumpToSentence = useCallback((index: number) => {
    registerUserActivity();
    if (index >= 0 && index < sentences.length) {
      stopRecording();
      window.speechSynthesis.cancel();
      setCurrentSentenceIndex(index);
    }
  }, [sentences.length, stopRecording, registerUserActivity]);

  return {
    isShadowingActive,
    toggleShadowing,
    sentences,
    currentSentenceIndex,
    currentSentence,
    wordStatuses,
    isListening,
    isTtsPlaying,
    hasTtsFinished,
    transcript,
    isSentenceComplete,
    ttsRate,
    cycleTtsRate,
    setPageText,
    playSentenceTts,
    playWordTts,
    startRecording,
    stopRecording,
    jumpToSentence
  };
};
